import React, { useEffect } from "react";
import { Shield, ShieldCheck } from "lucide-react";
import { useQzTrayConnection } from "../../../hooks/qztray/useQzTrayConnection";

/**
 * 🌐 ConnectionEndpointBox
 *
 * Muestra el host y puerto del cliente QZ Tray actualmente detectado.
 * Compatible con modo local y modo nube (seguro).
 */
const ConnectionEndpointBox: React.FC = () => {
  const { status, fetchStatus } = useQzTrayConnection();

  // 🚀 Obtener estado inicial del cliente QZ Tray
  useEffect(() => {
    fetchStatus();
  }, []);

  // 🧩 Endpoint reportado por backend o valor por defecto
  const endpoint = status?.endpoint || "";

  // ===========================================================
  // 🧠 Parsear protocolo, host y puerto de forma segura
  // ===========================================================
  const parsed = (() => {
    if (!endpoint) return { protocol: "", host: "—", port: "—" };

    // Asegurar un esquema válido para usar URL()
    const normalized =
      endpoint.startsWith("ws://") || endpoint.startsWith("wss://")
        ? endpoint
        : `ws://${endpoint}`;

    try {
      const url = new URL(normalized);
      return {
        protocol: url.protocol.replace(":", ""), // ws o wss
        host: url.hostname,
        port: url.port || (url.protocol === "wss:" ? "443" : "8181"),
      };
    } catch {
      return { protocol: "", host: endpoint, port: "—" };
    }
  })();

  // ===========================================================
  // 🛡️ Determinar modo de conexión
  // ===========================================================
  const isSecure = parsed.protocol === "wss";
  const connectionLabel = isSecure ? "Modo Seguro (SSL)" : "Modo Local";
  const colorClass = isSecure
    ? "text-green-600 dark:text-green-400"
    : "text-blue-600 dark:text-blue-400";

  return (
    <div className="rounded-sm border border-stroke bg-white dark:border-strokedark dark:bg-boxdark shadow-md p-5 flex flex-col items-center justify-center transition-colors">
      {/* 🔹 Encabezado */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${
            isSecure
              ? "bg-green-100 dark:bg-green-900/40"
              : "bg-blue-100 dark:bg-blue-900/40"
          }`}
        >
          {isSecure ? (
            <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
          ) : (
            <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          )}
        </div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Endpoint QZ Tray
        </h4>
      </div>

      {/* 🔸 Host y puerto */}
      <span
        className={`text-sm font-semibold ${colorClass}`}
      >
        {parsed.host}:{parsed.port}
      </span>

      {/* 🔸 Protocolo / modo */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {connectionLabel}
      </p>

      {/* 🔸 Descripción */}
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 truncate max-w-full">
        {endpoint || "Esperando conexión..."}
      </p>
    </div>
  );
};

export default ConnectionEndpointBox;
